import { useCallback, useMemo } from "react";
import { Helmet } from "react-helmet";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  message,
  Button,
  Card,
  Divider,
  Flex,
  Space,
  Spin,
  Tooltip,
  Typography,
} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import store from "store";

import { useConfigSelector } from "hooks/useSelector";
import { useGenerateOtp, useSingleLogin } from "queries";
import { decodeUriWithAtob, findHrCompanies } from "utils/helper";
import logoGajihub from "assets/images/logo.png";
import TwoFactorRequiredForm from "components/apps/Users/Form/TwoFactorRequiredForm";
import useDefaultPath from "components/apps/Login/hooks/useDefaultPath";

const { Title, Text } = Typography;

const Page = ({
  emailUser = "",
  isOnModal = false,
  isTemporary = false,
  onSuccessCallback,
}) => {
  const { t } = useTranslation();
  const paramsURL = useParams();
  const navigate = useNavigate();
  const defaultPath = useDefaultPath();
  const [messageApi, contextHolder] = message.useMessage();
  const { isTabView, isMobileView } = useConfigSelector();
  const generateOtpMutation = useGenerateOtp();
  const singleLoginMutation = useSingleLogin({ withSetEndpoint: true });

  const from = store.get("app.previousPathname") || "/dashboard";

  const decodeParamsURL = useMemo(() => {
    const decodeParamsURL = decodeUriWithAtob(paramsURL?.email);
    return decodeParamsURL;
  }, [paramsURL]);

  const email = useMemo(() => {
    return emailUser || decodeParamsURL?.email || "";
  }, [emailUser, decodeParamsURL]);

  const temporary = useMemo(() => {
    return isTemporary || `${decodeParamsURL?.temporary}` === "1";
  }, [decodeParamsURL, isTemporary]);

  const submitHandler = useCallback(
    (values, callback) => {
      const payload = {
        password: values.code,
        email,
        is_otp: 1,
        remember_me: Number(temporary),
      };
      singleLoginMutation.mutate(payload, {
        onSuccess: ({ data }) => {
          if (data?.success) {
            if (typeof onSuccessCallback === "function") {
              onSuccessCallback();
            }
            const companies = data?.data?.companies || [];
            const filteredCompanies = findHrCompanies(companies);
            if (filteredCompanies.length === 1) {
              navigate(defaultPath(data?.data?.init) || from, {
                replace: true,
              });
            } else {
              navigate("/user/select-company", { replace: true });
            }
            if (isOnModal) {
              window.location.reload(true);
            }
          } else {
            if (typeof callback === "function") {
              callback();
            }
            messageApi.error(data?.message || "Failed to verify code");
          }
        },
        onError: (e) => {
          if (typeof callback === "function") {
            callback();
          }
          messageApi.error(e?.message || e?.data?.message);
        },
      });
    },
    [
      from,
      singleLoginMutation,
      navigate,
      defaultPath,
      isOnModal,
      email,
      onSuccessCallback,
      temporary,
      messageApi,
    ]
  );

  const handleResendOTP = useCallback(
    ({ via, callback }) => {
      const payload = { email };
      if (via) {
        payload.via = via;
      }
      generateOtpMutation.mutate(payload, {
        onSuccess: (response) => {
          if (response.data.success) {
            if (callback) {
              callback();
            }
            let channelMessage = "";
            let viaMessage = via;
            if (via) {
              if (via === "wa") {
                viaMessage = "Whatsapp";
              }
              if (via !== "email") {
                viaMessage = `${t(
                  "automaticSequencing.number"
                ).toLowerCase()} ${viaMessage}`;
              }
              channelMessage = t(
                "users.otp_has_been_resent_to_your_via_registered_channel",
                {
                  channel: viaMessage,
                }
              );
            } else {
              channelMessage = t(
                "users.otp_has_been_resent_to_your_via_registered_channel",
                {
                  channel: "email",
                }
              );
            }
            message.success(channelMessage);
          } else {
            messageApi.error(response.data?.message || "Failed to verify code");
          }
        },
        onError: (error) => {
          messageApi.error(error?.data?.message || "Failed to verify code");
        },
      });
    },
    [generateOtpMutation, messageApi, email, t]
  );

  const backToLogin = useCallback(() => {
    navigate("/user/login", {
      replace: true,
    });
  }, [navigate]);

  return (
    <>
      <Helmet title="Two Factor Required" />
      <Flex
        style={{ width: "100%", padding: 12, marginBottom: 80 }}
        justify="center"
        align="center"
      >
        <Spin spinning={false}>
          <Card style={{ width: isMobileView || isTabView ? "100%" : 550 }}>
            <Space direction="vertical" size={16}>
              <Flex align="center" justify="space-between">
                <Tooltip title={t("button.back")}>
                  <Button
                    shape="circle"
                    size="large"
                    icon={<ArrowLeftOutlined />}
                    onClick={backToLogin}
                  />
                </Tooltip>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://gajihub.com"
                >
                  <img
                    width={isMobileView || isTabView ? 100 : 130}
                    src={logoGajihub}
                    alt="Gajihub"
                  />
                </a>
              </Flex>
              <Divider style={{ marginTop: 0, marginBottom: 0 }} />
              <Alert
                message={t(
                  "users.application_detect_sigin_from_other_different_browser"
                )}
                type="info"
                showIcon
              />
              <Title
                style={{ marginBottom: 0, marginTop: 0 }}
                className="text-center"
                level={3}
              >
                {t("users.verification_with_otp")}
              </Title>
              <Text style={{ textAlign: "center", display: "block" }}>
                <Trans
                  i18nKey="users.two_factor_verification_code_has_been_sent_to_your_email"
                  values={{
                    email,
                  }}
                  components={{
                    strong: <strong />,
                  }}
                />
              </Text>
            </Space>
            <TwoFactorRequiredForm
              onSubmit={submitHandler}
              loading={singleLoginMutation?.isLoading}
              onResend={handleResendOTP}
              email={email}
              resendLoading={generateOtpMutation.isLoading}
            />
          </Card>
        </Spin>
      </Flex>
      {contextHolder}
    </>
  );
};

export default Page;
